import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { formatEuros } from '../../../lib/format'

// The ledger follows the orderStats model: revenue_cents is PRODUCT sales
// (goods + verpakkingskosten, ex VAT) and gross_margin_* the product margin.
// Delivery fees are sales but never product — served beside them.
interface DailyLedgerSummary {
  revenue_cents: number
  cogs_cents: number
  gross_margin_cents: number
  gross_margin_percentage: number
  delivery_fee_ex_vat_cents: number | null
  order_count: number
  lines_without_cogs_count: number
  total_production_cost_cents: number
}

interface DaySummaryBarProps {
  summary: DailyLedgerSummary
}

// Null-tolerant wrapper: ledger fields can be absent while a day is open.
const fmt = (cents: number | null | undefined) => formatEuros(cents ?? 0)

const DaySummaryBar = ({ summary }: DaySummaryBarProps) => {
  const {
    revenue_cents,
    cogs_cents,
    gross_margin_cents,
    gross_margin_percentage,
    delivery_fee_ex_vat_cents,
    order_count,
    lines_without_cogs_count,
    total_production_cost_cents,
  } = summary

  const marginColor =
    gross_margin_percentage >= 60
      ? 'text-green-700'
      : gross_margin_percentage >= 40
      ? 'text-yellow-700'
      : 'text-red-700'

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-6 text-sm">
        <div>
          <span className="text-gray-500">Product sales</span>
          <p className="font-semibold text-gray-900">{fmt(revenue_cents)}</p>
        </div>
        <div>
          <span className="text-gray-500">COGS</span>
          <p className="font-semibold text-gray-900">{fmt(cogs_cents)}</p>
        </div>
        <div>
          <span className="text-gray-500">Product margin</span>
          <p className={`font-semibold ${marginColor}`}>
            {fmt(gross_margin_cents)}{' '}
            <span className="text-xs font-normal">({gross_margin_percentage.toFixed(1)}%)</span>
          </p>
        </div>
        {(delivery_fee_ex_vat_cents ?? 0) > 0 && (
          <div>
            <span className="text-gray-500">Delivery fees</span>
            <p className="font-semibold text-gray-900">{fmt(delivery_fee_ex_vat_cents)}</p>
          </div>
        )}
        <div>
          <span className="text-gray-500">Orders</span>
          <p className="font-semibold text-gray-900">{order_count}</p>
        </div>
        <div>
          <span className="text-gray-500">Production cost</span>
          <p className="font-semibold text-gray-900">{fmt(total_production_cost_cents)}</p>
        </div>
        {lines_without_cogs_count > 0 && (
          <div className="flex items-center gap-1 text-yellow-700">
            <ExclamationCircleIcon className="h-4 w-4" />
            <span>{lines_without_cogs_count} line{lines_without_cogs_count > 1 ? 's' : ''} without cost data</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default DaySummaryBar
